import jsPDF from "jspdf";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { PDFDocument, PDFName, PDFArray, PDFDict, PDFString, PDFNumber } from "pdf-lib";

/**
 * Dynamically loads the Noto Sans JP font for Japanese character support in PDFs.
 * Uses the FontFace API to load the font from Google Fonts CDN.
 * Skips loading if the font is already available in the document.
 */
const loadJapaneseFont = async (): Promise<void> => {
  // Check if font is already loaded to avoid redundant network requests
  if (document.fonts.check('16px "Noto Sans JP"')) {
    return;
  }
  
  try {
    const font = new FontFace(
      'Noto Sans JP',
      'url(https://fonts.gstatic.com/s/notosansjp/v52/-F6jfjtqLzI2JPCgQBnw7HFyzSD-AsregP8VFBEj75s.ttf)',
      { weight: '400', style: 'normal' }
    );
      
    await font.load();
    
    document.fonts.add(font);
    
    await document.fonts.ready;
    
    console.log('Japanese font loaded successfully');
  } catch (error) {
    console.warn('Failed to load Japanese font:', error);
  }
};

/**
 * Generates a PDF from an HTML element and optionally merges it with Terms & Conditions document.
 * Supports Japanese content, clickable hyperlinks, and proper styling preservation.
 * 
 * @param appInsights - Application Insights plugin for telemetry tracking
 * @param fileName - The ID of the HTML element to convert and the output filename
 * @param noOfItems - Number of items in the quotation (used for tracking)
 * @param termsAndConditionsDocument - Optional T&C PDF document to append
 */
export async function makePdf(
  appInsights: ReactPlugin,
  fileName: string,
  noOfItems: number,
  termsAndConditionsDocument: Uint8Array = new Uint8Array()
) {
  const pdfContainer = document.getElementById(fileName);
  if (!pdfContainer) return;

  try {
    appInsights.trackEvent({ name: `In PrintQuotation.tsx, Create PDF file of ${fileName}.` });

    // DPR (Detailed Price Report) requires different scaling for T&C pages
    const isDPR = fileName.includes('_DPR');
    
    // Detect Japanese characters using Unicode ranges (Hiragana, Katakana, Kanji, etc.)
    const pdfContent = pdfContainer.textContent || '';
    const hasJapaneseContent = /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\u4e00-\u9faf\u3400-\u4dbf]/.test(pdfContent);
    
    // Pre-load Japanese font to ensure proper rendering
    await loadJapaneseFont();
    
    // Clone the container to apply PDF-specific styles without affecting the UI
    const clonedContainer = pdfContainer.cloneNode(true) as HTMLElement;
    
    // Font stack with Japanese font priority, falling back to system fonts
    const fontFamily = '"Noto Sans JP", "Hiragino Sans", "Yu Gothic", "Meiryo", "Segoe UI", Arial, sans-serif';
    
    // Set base container styles for A4 page width (210mm)
    clonedContainer.style.cssText = `
      width: 210mm;
      font-family: ${fontFamily};
      font-size: 12px;
      line-height: 1.4;
      color: #333;
      background: #fff;
      padding: 0mm;
      box-sizing: border-box;
    `;
    
    // Apply consistent font family to all child elements
    const allElements = clonedContainer.querySelectorAll('*');
    allElements.forEach((el) => {
      const element = el as HTMLElement;
      element.style.fontFamily = fontFamily;
    });
    
    // Set #333 color explicitly for paragraph and text-containing elements with !important
    const textElements = clonedContainer.querySelectorAll('p, span, td, div, li');
    textElements.forEach((el) => {
      const element = el as HTMLElement;
      element.style.setProperty('color', '#333', 'important');
    });
    
    // Enhance bold text visibility using text-shadow technique for better PDF rendering
    const boldElements = clonedContainer.querySelectorAll('b, strong, [style*="font-weight"]');
    boldElements.forEach((el) => {
      const element = el as HTMLElement;
      element.style.textShadow = '0 0 0 currentColor';
      element.style.fontWeight = '700';
      element.style.setProperty('color', '#000', 'important');
    });
    
    // Style table headers with consistent formatting
    const tableHeaders = clonedContainer.querySelectorAll('th');
    tableHeaders.forEach((th) => {
      const element = th as HTMLElement;
      element.style.textShadow = '0 0 0 currentColor';
      element.style.fontWeight = '700';
      element.style.setProperty('color', '#000', 'important');
      element.style.textTransform = 'capitalize';
      element.style.fontSize = '11px';
      element.style.textAlign = 'left';
      element.style.backgroundColor = '#f3f2f1';
    });
    
    // Style hyperlinks with Microsoft blue color
    const links = clonedContainer.querySelectorAll('a');
    links.forEach((link) => {
      const href = link.getAttribute('href');
      const textContent = link.textContent?.trim() || '';
      
      // Email pattern to detect email addresses
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      
      // If href is "#" or empty but contains an email, convert to mailto link
      if ((!href || href === '#') && emailPattern.test(textContent)) {
        link.setAttribute('href', `mailto:${textContent}`);
        link.style.setProperty('color', '#0078d4', 'important');
        link.style.textDecoration = 'underline';
      } else if (href && href !== '#') {
        link.style.setProperty('color', '#0078d4', 'important');
        link.style.textDecoration = 'underline';
      }
    });
    
    // Apply border styles to table cells with .table-border class
    const tableBorderCells = clonedContainer.querySelectorAll('.table-border');
    tableBorderCells.forEach((cell) => {
      const cellElement = cell as HTMLElement;
      cellElement.style.setProperty('border', '1px solid lightgray', 'important');
    });
    
    // Style data tables with borders and collapsed spacing
    const dataTables = clonedContainer.querySelectorAll('table.table, table.border-color');
    dataTables.forEach((table) => {
      const tableElement = table as HTMLElement;
      tableElement.style.width = '100%';
      tableElement.style.setProperty('border-spacing', '0', 'important');
      tableElement.style.setProperty('border-collapse', 'collapse', 'important');
      tableElement.style.setProperty('border', '1px solid lightgray', 'important');
      
      const tableCells = tableElement.querySelectorAll('td');
      tableCells.forEach((td) => {
        const cellElement = td as HTMLElement;
        cellElement.style.fontSize = '11px';
        cellElement.style.setProperty('border', '1px solid lightgray', 'important');
      });
      
      const tableHeaderCells = tableElement.querySelectorAll('th');
      tableHeaderCells.forEach((th) => {
        const cellElement = th as HTMLElement;
        cellElement.style.setProperty('border', '1px solid lightgray', 'important');
      });
    });
    
    // Style layout tables (borderless) used for positioning content
    const layoutTables = clonedContainer.querySelectorAll('table.table-no-border');
    layoutTables.forEach((table) => {
      const tableElement = table as HTMLElement;
      tableElement.style.setProperty('border', 'none', 'important');
      tableElement.style.setProperty('border-collapse', 'collapse', 'important');
      tableElement.style.fontSize = '12px';
      
      const allCells = tableElement.querySelectorAll('th, td');
      allCells.forEach((cell) => {
        const cellElement = cell as HTMLElement;
        cellElement.style.setProperty('border', 'none', 'important');
      });
      
      const tdBorderCells = tableElement.querySelectorAll('.td-border');
      tdBorderCells.forEach((td) => {
        const cellElement = td as HTMLElement;
        cellElement.style.setProperty('border', 'none', 'important');
        cellElement.style.padding = '0';
      });
    });
    
    // Apply default padding and alignment to all table cells
    const cells = clonedContainer.querySelectorAll('th, td');
    cells.forEach((cell) => {
      const cellElement = cell as HTMLElement;
      if (!cellElement.classList.contains('td-border')) {
        cellElement.style.padding = '4px 10px';
      }
      cellElement.style.verticalAlign = 'middle';
      if (!cellElement.classList.contains('text-right') && 
          !cellElement.classList.contains('text-center') &&
          !cellElement.classList.contains('text-left')) {
        cellElement.style.textAlign = 'left';
      }
    });
    
    // Apply CSS utility class styles (text alignment, flexbox, widths, margins)
    const textRightElements = clonedContainer.querySelectorAll('.text-right');
    textRightElements.forEach((el) => {
      (el as HTMLElement).style.textAlign = 'right';
    });
    
    const textCenterElements = clonedContainer.querySelectorAll('.text-center');
    textCenterElements.forEach((el) => {
      (el as HTMLElement).style.textAlign = 'center';
    });
    
    const textLeftElements = clonedContainer.querySelectorAll('.text-left');
    textLeftElements.forEach((el) => {
      (el as HTMLElement).style.textAlign = 'left';
    });
    
    const flexElements = clonedContainer.querySelectorAll('.flex');
    flexElements.forEach((el) => {
      (el as HTMLElement).style.display = 'flex';
    });
    
    const spaceBetweenElements = clonedContainer.querySelectorAll('.space-between');
    spaceBetweenElements.forEach((el) => {
      (el as HTMLElement).style.justifyContent = 'space-between';
    });
    
    const w50Elements = clonedContainer.querySelectorAll('.w-50');
    w50Elements.forEach((el) => {
      (el as HTMLElement).style.width = '50%';
    });
    
    const w100Elements = clonedContainer.querySelectorAll('.w-100, .width100');
    w100Elements.forEach((el) => {
      (el as HTMLElement).style.width = '100%';
    });
    
    const rowElements = clonedContainer.querySelectorAll('.row');
    rowElements.forEach((el) => {
      const element = el as HTMLElement;
      element.style.width = '101%';
      element.style.display = 'flex';
    });
    
    const tDataWithoutBorderElements = clonedContainer.querySelectorAll('.tDataWithoutBorder');
    tDataWithoutBorderElements.forEach((el) => {
      const element = el as HTMLElement;
      element.style.textAlign = 'right';
      element.style.border = '0';
    });
    
    const backgroundStyleElements = clonedContainer.querySelectorAll('.background-style');
    backgroundStyleElements.forEach((el) => {
      (el as HTMLElement).style.backgroundColor = 'rgba(217, 217, 217, 0.633)';
    });
    
    const top30Elements = clonedContainer.querySelectorAll('.top30');
    top30Elements.forEach((el) => {
      (el as HTMLElement).style.marginTop = '30px';
    });
    
    const top20Elements = clonedContainer.querySelectorAll('.top20');
    top20Elements.forEach((el) => {
      (el as HTMLElement).style.marginTop = '20px';
    });
    
    const floatRightElements = clonedContainer.querySelectorAll('.float-right');
    floatRightElements.forEach((el) => {
      const element = el as HTMLElement;
      element.style.float = 'right';
      element.style.margin = '5px';
    });
    
    const flexEndElements = clonedContainer.querySelectorAll('.flexEnd');
    flexEndElements.forEach((el) => {
      const element = el as HTMLElement;
      element.style.display = 'flex';
      element.style.justifyContent = 'end';
    });
    
    const w30Elements = clonedContainer.querySelectorAll('.w-30');
    w30Elements.forEach((el) => {
      (el as HTMLElement).style.width = '30%';
    });
    
    const w40Elements = clonedContainer.querySelectorAll('.w-40');
    w40Elements.forEach((el) => {
      (el as HTMLElement).style.width = '40%';
    });
    
    const w60Elements = clonedContainer.querySelectorAll('.w-60');
    w60Elements.forEach((el) => {
      (el as HTMLElement).style.width = '60%';
    });
    
    const w70Elements = clonedContainer.querySelectorAll('.w-70');
    w70Elements.forEach((el) => {
      (el as HTMLElement).style.width = '69%';
    });
    
    const w80Elements = clonedContainer.querySelectorAll('.w-80');
    w80Elements.forEach((el) => {
      (el as HTMLElement).style.width = '80%';
    });
    
    const width33Elements = clonedContainer.querySelectorAll('.width33');
    width33Elements.forEach((el) => {
      (el as HTMLElement).style.width = '33%';
    });

    // Convert relative image paths to absolute URLs for proper PDF rendering
    const images = clonedContainer.querySelectorAll('img');
    images.forEach((img) => {
      const src = img.getAttribute('src');
      if (src && !src.startsWith('http') && !src.startsWith('data:')) {
        img.setAttribute('src', window.location.origin + src);
      }
    });
    
    // Render cloned container off-screen to calculate accurate element positions for links
    const tempContainer = document.createElement('div');
    tempContainer.style.cssText = 'position: absolute; left: -9999px; top: 0;';
    tempContainer.appendChild(clonedContainer);
    document.body.appendChild(tempContainer);
    
    // Collect link positions for adding clickable annotations to the PDF
    const linkData: Array<{ href: string; rect: DOMRect; text: string }> = [];
    const containerRect = clonedContainer.getBoundingClientRect();
    
    // Capture explicit anchor links
    const allLinks = clonedContainer.querySelectorAll('a');
    allLinks.forEach((link) => {
      const href = link.getAttribute('href');
      const textContent = link.textContent?.trim() || '';
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      
      if (href && href !== '#') {
        linkData.push({
          href: href.startsWith('http') || href.startsWith('mailto:') ? href : `https://${href}`,
          rect: link.getBoundingClientRect(),
          text: textContent
        });
      } else if (emailPattern.test(textContent)) {
        // Handle email addresses with href="#" or no href
        linkData.push({
          href: `mailto:${textContent}`,
          rect: link.getBoundingClientRect(),
          text: textContent
        });
      }
    });
    
    // Detect URL-like text content and treat as clickable links
    const urlPattern = /^(https?:\/\/|www\.)[^\s]+$/i;
    const emailPatternForText = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const potentialLinkElements = clonedContainer.querySelectorAll('a:not([href]), a[href="#"], button, span[role="link"], .ms-Link');
    potentialLinkElements.forEach((element) => {
      const textContent = element.textContent?.trim() || '';
      if (urlPattern.test(textContent)) {
        const formattedUrl = textContent.startsWith('http') ? textContent : `https://${textContent}`;
        (element as HTMLElement).style.color = '#0078d4';
        (element as HTMLElement).style.textDecoration = 'underline';
        linkData.push({
          href: formattedUrl,
          rect: element.getBoundingClientRect(),
          text: textContent
        });
      } else if (emailPatternForText.test(textContent)) {
        // Handle email addresses in text
        (element as HTMLElement).style.color = '#0078d4';
        (element as HTMLElement).style.textDecoration = 'underline';
        linkData.push({
          href: `mailto:${textContent}`,
          rect: element.getBoundingClientRect(),
          text: textContent
        });
      }
    });
    
    // PDF configuration for A4 portrait format
    const orientation = "portrait";
    const format = "a4" as const;
    
    const pdf = new jsPDF({
      orientation,
      unit: 'mm',
      format,
      compress: true,
      putOnlyUsedFonts: true,
    });
    
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 5; // 5mm margin on all sides
    const contentWidth = pageWidth - (margin * 2);
    
    // Convert HTML to PDF using html2canvas for rendering
    await new Promise<void>((resolve, reject) => {
      pdf.html(clonedContainer, {
        callback: function() {
          resolve();
        },
        x: margin,
        y: 0,
        width: contentWidth,
        windowWidth: clonedContainer.scrollWidth || 800,
        autoPaging: 'text',
        margin: [margin, margin, margin, margin],
        html2canvas: {
          scale: 0.235, // Scaling factor for proper A4 fit (adjusted from standard 0.264583)
          useCORS: true,
          allowTaint: false, // Set to false to prevent tainting issues with cross-origin resources
          logging: false,
          letterRendering: true,
        },
        fontFaces: [
          {
            family: 'Noto Sans JP',
            style: 'normal' as const,
            weight: 400,
            src: [{ url: 'https://fonts.gstatic.com/s/notosansjp/v52/-F6jfjtqLzI2JPCgQBnw7HFyzSD-AsregP8VFBEj75s.ttf', format: 'truetype' as const }]
          }
        ], 
      }).catch((error) => {
        reject(error);
      });
    }).finally(() => {
      // Clean up temporary container
      document.body.removeChild(tempContainer);
    });
    
    // Use pdf-lib for merging PDFs and adding link annotations
    const mergedPdf = await PDFDocument.create();
    const quotationData = await PDFDocument.load(await pdf.output("arraybuffer"));
    const quotationPages = await mergedPdf.copyPages(quotationData, quotationData.getPageIndices());
    quotationPages.forEach(page => mergedPdf.addPage(page));
    
    // Convert HTML element positions to PDF coordinates and add clickable link annotations
    const scale = 0.235;
    const pdfPageHeight = 841.89; // A4 height in points (297mm × 2.83465 pts/mm)
    const marginPts = margin * 2.83465; // Convert margin from mm to points
    
    linkData.forEach((linkInfo) => {
      try {
        // Calculate link position relative to container
        const relativeX = linkInfo.rect.left - containerRect.left;
        const relativeY = linkInfo.rect.top - containerRect.top;
        
        // Convert pixel coordinates to PDF points (1 point = 1/72 inch)
        const xPts = marginPts + (relativeX * scale * 2.83465);
        const yFromTop = relativeY * scale * 2.83465;
        const widthPts = linkInfo.rect.width * scale * 2.83465;
        const heightPts = linkInfo.rect.height * scale * 2.83465;
        
        // Determine which page the link falls on based on content height
        const contentHeight = pdfPageHeight - (marginPts * 2);
        const pageIndex = Math.floor(yFromTop / contentHeight);
        const yOnPageFromTop = yFromTop - (pageIndex * contentHeight) + marginPts;
        
        // PDF coordinates start from bottom-left, so invert Y axis
        const yPts = pdfPageHeight - yOnPageFromTop - heightPts;
        
        const pages = mergedPdf.getPages();
        if (pageIndex < pages.length) {
          const page = pages[pageIndex];
          
          // Create URI link annotation for clickable hyperlinks in the PDF
          const linkAnnotation = page.doc.context.obj({
            Type: 'Annot',
            Subtype: 'Link',
            Rect: [xPts, yPts, xPts + widthPts, yPts + heightPts],
            Border: [0, 0, 0], // No visible border
            A: {
              Type: 'Action',
              S: 'URI',
              URI: PDFString.of(linkInfo.href),
            },
          });
          
          // Add annotation to the page's Annots array
          const annots = page.node.get(PDFName.of('Annots'));
          if (annots instanceof PDFArray) {
            annots.push(page.doc.context.register(linkAnnotation));
          } else {
            page.node.set(PDFName.of('Annots'), page.doc.context.obj([page.doc.context.register(linkAnnotation)]));
          }
        }
      } catch (linkError) {
        console.warn('Failed to add link annotation:', linkError);
      }
    });

    // Append Terms & Conditions document if provided
    if (termsAndConditionsDocument.byteLength > 0) {
      try {
        const termsPdf = await PDFDocument.load(termsAndConditionsDocument);
        const termsCopiedPages = await mergedPdf.copyPages(termsPdf, termsPdf.getPageIndices());
        
        termsCopiedPages.forEach(page => {
          // Scale T&C pages to fit A4 (skip scaling for DPR documents)
          if (!isDPR) {
            const { width: w, height: h } = page.getSize();
            const scale = Math.min(
              (pageWidth * 2.83465) / w,
              (pageHeight * 2.83465) / h
            ) * 0.95; // 95% to ensure margins
            page.scale(scale, scale);
          }
          mergedPdf.addPage(page);
        });
      } catch (error: any) {
        appInsights.trackException({ error, properties: { name: `In ${fileName}. error adding T&C.` } });
      }
    }

    // Generate final PDF and trigger download
    const pdfBytes = await mergedPdf.save();
    const blob = new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.pdf`;
    link.style.display = "none";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up object URL to prevent memory leaks

    appInsights.trackEvent({ name: `In PrintQuotation.tsx, PDF created successfully for ${fileName}.` });

  } catch (error: any) {
    appInsights.trackException({ error, properties: { name: `In ${fileName}. PDF creation error.` } });
    console.error('PDF creation error:', error);
    return error;
  }
}